import { CCILayer, YieldLayer } from 'store/types/types';

export type GroupedCCILayers = {
  [year: string]: {
    [month: string]: CCILayer[];
  };
};

/** Group layers by a given criteria
 *
 * @param layers The layers to be gruped
 * @param criteria Criteria to group by. It can be a key of the layers,
 * or a function that get a layer and returns a string to be used as key
 */
export const groupLayersBy = <T>(layers: T[], criteria: string | ((layer: T) => string)) => {
  const groupedLayers = {} as Record<string, T[]>;

  layers.forEach((layer) => {
    const key = typeof criteria === 'string' ? layer[criteria] : criteria(layer);
    groupedLayers[key] = (groupedLayers[key] || []).concat(layer);
  });

  return groupedLayers;
};

/** Returns the long form of a layer's month, in English */
export const getLayerMonth = ({ eventDate }: CCILayer | YieldLayer) => {
  return new Date(eventDate).toLocaleDateString('en', { month: 'long' });
};

export const groupCciLayersByMonths = (cciLayersGroupedByYear: { [year: number]: CCILayer[] }) => {
  const cciLayersByYearAndMonth = {} as GroupedCCILayers;

  Object.entries(cciLayersGroupedByYear).forEach(([year, yearGroup]) => {
    cciLayersByYearAndMonth[year] = groupLayersBy<CCILayer>(yearGroup, getLayerMonth);
  });

  return cciLayersByYearAndMonth;
};

export const sortCciLayersMonthsGroupsByDate = (cciLayersByYearAndMonth: GroupedCCILayers) => {
  const cciLayersByYearAndMonthSorted = {} as GroupedCCILayers;

  Object.entries(cciLayersByYearAndMonth).forEach(([year, yearGroup]) => {
    cciLayersByYearAndMonthSorted[year] = yearGroup;
    Object.entries(yearGroup).forEach(([month, monthGroup]) => {
      cciLayersByYearAndMonthSorted[year][month] = monthGroup.toSorted((layerA, layerB) => {
        return new Date(layerA.eventDate).getTime() - new Date(layerB.eventDate).getTime();
      });
    });
  });

  return cciLayersByYearAndMonthSorted;
};

export const parseAvailableLayers = ({
  yieldLayers,
  cciLayers
}: {
  yieldLayers: YieldLayer[];
  cciLayers: CCILayer[];
}) => {
  const yieldLayersByCropId = groupLayersBy(yieldLayers, 'cropId');
  const cciLayersByYear = groupLayersBy(cciLayers, 'year');
  const cciLayersByYearAndMonth = groupCciLayersByMonths(cciLayersByYear);
  const cciLayersByYearAndMonthSorted = sortCciLayersMonthsGroupsByDate(cciLayersByYearAndMonth);

  return { yield: yieldLayersByCropId, cci: cciLayersByYearAndMonthSorted };
};
